import { WhatsApp, Email, Phone, Business, Facebook, Instagram, VideoLibrary, Help, ContactSupport, Description } from '@mui/icons-material';
import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import XIcon from '@mui/icons-material/X';

const Footer: React.FC = () => {
  const { t, i18n } = useTranslation();
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    {
      name: 'Facebook',
      icon: <Facebook />,
      url: 'https://www.facebook.com/profile.php?id=61580674130494',
      color: 'hover:bg-blue-600 hover:text-white'
    },
    {
      name: 'Instagram',
      icon: <Instagram />,
      url: 'https://www.instagram.com/bazaar_marketplace/',
      color: 'hover:bg-gradient-to-r hover:from-purple-600 hover:via-pink-600 hover:to-orange-500 hover:text-white'
    },
  
    {
      name: 'TikTok',
      icon: <VideoLibrary />,
      url: 'https://www.tiktok.com/@bazaar.marketplac?_r=1&_t=ZS-92srT4A9xpq',
      color: 'hover:bg-gray-800 hover:text-white'
    }
  ];

  const contactLinks = [
    {
      icon: <Email />,
      text: 'support@bazaar-marketplace.com',
      href: 'mailto:support@bazaar-marketplace.com'
    },
    {
      icon: <Phone />,
      text: '+20 150 332 3006',
      href: 'tel:+201503323006'
    },
    {
      icon: <WhatsApp />,
      text: t('footer.whatsapp'),
      href: 'https://wa.me/201503323006'
    }
  ];

  // Updated Quick Links with all new legal pages
  const quickLinks = [
    { path: '/products', label: t('footer.links.products'), icon: null },
    { path: '/cart', label: t('footer.links.cart'), icon: null },
    { path: '/faq', label: t('footer.links.faq'), icon: <Help className="w-4 h-4" /> },
    { path: '/contact', label: t('footer.links.contact'), icon: <ContactSupport className="w-4 h-4" /> }
  ];

  // New Legal Links Section
  const legalLinks = [
    { path: '/terms-conditions', label: t('footer.links.terms'), icon: <Description className="w-4 h-4" /> },
    { path: '/terms-conditions-en', label: t('footer.links.terms_en'), icon: <Description className="w-4 h-4" /> },
    { path: '/policies', label: t('footer.links.policies'), icon: <Description className="w-4 h-4" /> },
    { path: '/policies-en', label: t('footer.links.policies_en'), icon: <Description className="w-4 h-4" /> }
  ];

  // Additional Links Section
  const additionalLinks = [
    { path: '/about', label: t('footer.links.about'), icon: null },
    { path: '/privacy-policy', label: t('footer.links.privacy'), icon: null },
    { path: '/return-policy', label: t('footer.links.returns'), icon: null },
    { path: '/shipping-policy', label: t('footer.links.shipping'), icon: null }
  ];

  return (
    <footer className="bg-gradient-to-b from-gray-900 to-black text-white py-12 border-t-4 border-primary">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-8">
          
          {/* Brand Info */}
          <div className="space-y-4 lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-gradient-to-r from-primary to-primary-dark p-3 rounded-xl shadow-lg">
                <Business className="w-8 h-8" />
              </div>
              <div>
                <h3 className="text-2xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                  {t('footer.brand')}
                </h3>
                <p className="text-sm text-gray-400 mt-1">
                  {i18n.language.startsWith('ar') ? 'السوق الرقمي المصري' : 'Egyptian Digital Marketplace'}
                </p>
              </div>
            </div>
            <p className="text-gray-300 leading-relaxed text-lg">
              {t('footer.description')}
            </p>
            <div className="bg-gray-800/50 backdrop-blur-sm px-4 py-3 rounded-xl mt-4">
              <div className="flex items-center gap-2">
                <Business className="w-5 h-5 text-primary" />
                <div>
                  <span className="text-sm font-medium text-gray-300 block">
                    {t('footer.taxId')}: <strong className="text-white">752-382-462</strong>
                  </span>
                  <span className="text-xs text-gray-400 block mt-1">
                    {i18n.language.startsWith('ar') ? 'سجل تجاري: 123456' : 'Commercial Register: 123456'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-bold mb-6 pb-3 border-b border-gray-700 flex items-center">
              <span className="bg-gradient-to-r from-primary to-primary-dark w-3 h-3 rounded-full mr-2"></span>
              {t('footer.linksTitle')}
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.path} 
                    className="group flex items-center gap-3 text-gray-300 hover:text-primary transition-all duration-300 hover:translate-x-2"
                  >
                    {link.icon && (
                      <span className="opacity-70 group-hover:opacity-100">
                        {link.icon}
                      </span>
                    )}
                    <span className="w-2 h-2 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                    <span className="group-hover:font-medium">{link.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="text-xl font-bold mb-6 pb-3 border-b border-gray-700 flex items-center">
              <span className="bg-gradient-to-r from-blue-500 to-cyan-500 w-3 h-3 rounded-full mr-2"></span>
              {i18n.language.startsWith('ar') ? 'شروط وسياسات' : 'Legal'}
            </h3>
            <ul className="space-y-3">
              {legalLinks.map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.path} 
                    className="group flex items-center gap-3 text-gray-300 hover:text-blue-400 transition-all duration-300 hover:translate-x-2"
                  >
                    {link.icon && (
                      <span className="opacity-70 group-hover:opacity-100">
                        {link.icon}
                      </span>
                    )}
                    <span className="w-2 h-2 bg-blue-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                    <span className="group-hover:font-medium text-sm">{link.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact & Social Media */}
          <div>
            <h3 className="text-xl font-bold mb-6 pb-3 border-b border-gray-700 flex items-center">
              <span className="bg-gradient-to-r from-green-500 to-emerald-500 w-3 h-3 rounded-full mr-2"></span>
              {t('footer.contactTitle')}
            </h3>
            <div className="space-y-4">
              {contactLinks.map((contact, index) => (
                <a 
                  key={index}
                  href={contact.href}
                  target={contact.href.includes('http') ? '_blank' : '_self'}
                  rel={contact.href.includes('http') ? 'noopener noreferrer' : ''}
                  className="flex items-center gap-3 p-3 rounded-xl bg-gray-800/30 hover:bg-gray-800/60 transition-all duration-300 hover:shadow-lg group"
                >
                  <div className={`p-2 rounded-lg ${
                    index === 0 ? 'bg-blue-500/20 group-hover:bg-blue-500/30' :
                    index === 1 ? 'bg-green-500/20 group-hover:bg-green-500/30' :
                    'bg-emerald-500/20 group-hover:bg-emerald-500/30'
                  }`}>
                    {contact.icon}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm text-gray-400">
                      {index === 0 ? t('footer.email') : 
                       index === 1 ? t('footer.phone') : 'WhatsApp'}
                    </div>
                    <div className="font-medium group-hover:text-primary transition-colors text-sm">
                      {contact.text}
                    </div>
                  </div>
                </a>
              ))}
              
              {/* Social Media */}
              <div className="mt-4">
                <p className="text-gray-300 text-sm mb-3">
                  {i18n.language.startsWith('ar') 
                    ? 'تابعنا على:'
                    : 'Follow us:'}
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {socialLinks.map((social, index) => (
                    <a
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex flex-col items-center justify-center p-2 rounded-xl bg-gray-800/50 transition-all duration-300 ${social.color} hover:scale-105`}
                      title={social.name}
                    >
                      <span className="text-lg">{social.icon}</span>
                      <span className="text-xs mt-1 opacity-90">{social.name}</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Links Bar */}
        <div className="mb-8 pt-6 border-t border-gray-800">
          <div className="flex flex-wrap justify-center gap-6">
            {additionalLinks.map((link, index) => (
              <Link
                key={index}
                to={link.path}
                className="text-gray-400 hover:text-primary text-sm transition-colors duration-300"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-800 my-6"></div>

        {/* Bottom Section */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="text-center md:text-left">
            <div className="text-gray-400 text-sm">
              &copy; {currentYear} {t('footer.brand')}. {t('footer.copyright')}
            </div>
            <div className="text-gray-500 text-xs mt-2">
              {i18n.language.startsWith('ar') 
                ? 'منصة تجارة إلكترونية متكاملة - جميع الخدمات متوفرة في مصر'
                : 'Complete E-commerce Platform - All services available in Egypt'}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-gray-400 text-sm">{t('footer.poweredBy')}</span>
              <a 
                href="https://codeocean.tech" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:text-primary-light font-bold text-lg transition-colors"
              >
                codeocean.tech
              </a>
            </div>
            <div className="text-xs text-gray-500 bg-gray-800/50 px-3 py-1 rounded-full">
              v1.2.0
            </div>
          </div>

          {/* Language & Theme Selector */}
          <div className="flex items-center gap-4">
            {/* Language Selector */}
            <div className="flex gap-2">
              <button
                onClick={() => i18n.changeLanguage('ar')}
                className={`px-3 py-1 rounded-lg text-sm transition-all duration-300 ${
                  i18n.language.startsWith('ar') 
                    ? 'bg-primary text-white shadow-lg' 
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
                title={i18n.language.startsWith('ar') ? 'اللغة العربية' : 'Arabic Language'}
              >
                العربية
              </button>
              <button
                onClick={() => i18n.changeLanguage('en')}
                className={`px-3 py-1 rounded-lg text-sm transition-all duration-300 ${
                  i18n.language.startsWith('en') 
                    ? 'bg-primary text-white shadow-lg' 
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
                title={i18n.language.startsWith('ar') ? 'اللغة الإنجليزية' : 'English Language'}
              >
                English
              </button>
            </div>
          </div>
        </div>

        {/* Microdata for SEO */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": t('footer.brand'),
            "description": t('footer.description'),
            "url": window.location.origin,
            "logo": `${window.location.origin}/logo.png`,
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+201503323006",
              "contactType": "customer service",
              "email": "support@bazaar-marketplace.com",
              "availableLanguage": ["Arabic", "English"],
              "hoursAvailable": "Mo-Su 10:00-22:00"
            },
            "sameAs": socialLinks.map(social => social.url),
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "EG",
              "addressLocality": "Cairo"
            },
            "taxID": "752-382-462",
            "foundingDate": "2024",
            "founder": "CODEOCEAN.TECH"
          })}
        </script>

        {/* Legal Notice */}
        <div className="mt-8 pt-6 border-t border-gray-800 text-center">
          <p className="text-xs text-gray-500">
            {i18n.language.startsWith('ar') 
              ? 'جميع الصفحات القانونية محدثة بتاريخ: ' + new Date().toLocaleDateString('ar-EG')
              : 'All legal pages updated as of: ' + new Date().toLocaleDateString('en-US')}
          </p>
          <p className="text-xs text-gray-400 mt-2">
            {i18n.language.startsWith('ar') 
              ? 'للاستفسارات القانونية يرجى التواصل عبر البريد الإلكتروني'
              : 'For legal inquiries please contact via email'}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;